import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';
import UpdateNormSetAnchors from '@graphql/mutations/UpdateNormSetAnchors';
import {
  AnchorLookupQuery,
  type ProductLookupRow,
} from '@graphql/queries/PerceptionModelQueries';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';

import AnchorPicker from './AnchorPicker';
import { remeasureNormSet, type Remeasure } from './normSetUtils';
import PerceptionModelError from './PerceptionModelError';

/**
 * Move the anchors a saved norm set is measured from.
 *
 * This is not a relabel. Every distance in the row was measured *from* the
 * anchors, so changing them re-measures all of them before anything is written,
 * and both columns are saved in one patch. New anchors beside old distances
 * would be a hundred numbers describing a comparison that was never made.
 *
 * Membership does not move. The same products stay in the set; only what they
 * are measured against changes. A different set of products is a new norm set.
 */

type Props = {
  normSetId: number;
  jobId: number;
  producerId: number;
  anchors: string[];
  /** current membership, kept exactly as it is */
  members: string[];
  /** how many translations hang off this norm set */
  translationCount: number;
  open: boolean;
  onClose: () => void;
};

/**
 * What the proposed anchors would do to the set, said before it happens.
 *
 * Split out so the editor itself stays about picking and saving. Nothing here
 * refuses the move — a product may anchor a set it also belongs to, and that is
 * a choice rather than a mistake. Two of these report a number that would be
 * stored as fact without being a measurement, and one is context.
 */
const AnchorGuards: React.FC<{
  result: Remeasure;
  memberCount: number;
  translationCount: number;
  emptySelection: boolean;
}> = ({ result, memberCount, translationCount, emptySelection }) => {
  const { unmeasured, warned } = result;

  return (
    <>
      {emptySelection && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          A norm set needs at least one anchor. With none there is nothing to
          measure the products against.
        </Alert>
      )}

      {unmeasured.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {unmeasured.length} product{unmeasured.length === 1 ? '' : 's'} cannot
          be placed against these anchors at all, so{' '}
          {unmeasured.length === 1 ? 'it' : 'they'} would be stored with no
          distance.
        </Alert>
      )}

      {warned.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {warned.length} of {memberCount} products fall outside at least one
          anchor&rsquo;s peer list, so their distance would be that
          anchor&rsquo;s bound rather than a measurement — an upper limit stored
          as fact. They are the farthest members.
        </Alert>
      )}

      {translationCount > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {translationCount} translation{translationCount === 1 ? '' : 's'} hang
          {translationCount === 1 ? 's' : ''} off this norm set. Their audiences
          and products are untouched, but they will read against the new
          comparison.
        </Alert>
      )}
    </>
  );
};

const AnchorEditor: React.FC<Props> = ({
  normSetId,
  jobId,
  producerId,
  anchors,
  members,
  translationCount,
  open,
  onClose,
}) => {
  const [next, setNext] = React.useState<string[]>(anchors);

  // Reopening after a cancel should not resume a half-made decision.
  React.useEffect(() => {
    if (open) setNext(anchors);
  }, [open, anchors]);

  /**
   * What the re-measure is actually run against, a beat behind the selection.
   *
   * Each lookup pulls a peer list per anchor — 8.2 kB a row, the heaviest field
   * in the table — so picking thirty anchors one click at a time would fire
   * thirty progressively larger queries to use only the last. The menu stays
   * open between clicks precisely to encourage that, so the settling has to
   * happen here.
   */
  const [settled, setSettled] = React.useState<string[]>(anchors);
  React.useEffect(() => {
    const t = setTimeout(() => setSettled(next), 350);
    return () => clearTimeout(t);
  }, [next]);

  const {
    data,
    loading: rowsLoading,
    error: rowsError,
  } = useQuery(AnchorLookupQuery, {
    variables: { jobId, names: settled },
    skip: !open || settled.length === 0,
  });

  const anchorRows: ProductLookupRow[] =
    data?.allPerceptionModelProductLookups?.nodes ?? [];

  // A failed lookup and a genuinely absent row look identical from `data`, and
  // the wrong one of those tells the analyst their anchors are not in the model
  // and every member is unplaceable. So nothing is concluded from an errored
  // read — the error itself is what is shown.
  const missing = React.useMemo(
    () =>
      rowsError
        ? []
        : settled.filter((n) => !anchorRows.some((r) => r.productName === n)),
    [settled, anchorRows, rowsError],
  );

  const result = React.useMemo(
    () => remeasureNormSet(members, anchorRows),
    [members, anchorRows],
  );

  const [save, { loading: saving, error: saveError }] = useMutation(
    UpdateNormSetAnchors,
    {
      onCompleted: onClose,
      // Without this the execute function rejects unhandled; saveError already
      // renders the reason.
      onError: () => {},
    },
  );

  const unchanged =
    next.length === anchors.length && next.every((n) => anchors.includes(n));

  // The distances on screen describe `settled`, so saving before the selection
  // has caught up would write numbers taken from a different set of anchors.
  const pending =
    settled.length !== next.length || settled.some((n) => !next.includes(n));

  const ready =
    next.length > 0 &&
    !pending &&
    !rowsLoading &&
    !rowsError &&
    missing.length === 0 &&
    anchorRows.length > 0 &&
    !unchanged;

  const onSave = () =>
    save({
      variables: {
        id: normSetId,
        // One patch. Split across two mutations, a failure between them would
        // leave the row in exactly the state this exists to prevent.
        patch: { anchors: next, normSet: result.normSet },
      },
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Change anchors</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          The anchors are what this norm set is measured from. Changing them
          re-measures all {members.length} distances and saves both together —
          the products in the set do not change.
        </DialogContentText>

        <AnchorPicker
          producerId={producerId}
          jobId={jobId}
          value={next}
          onChange={setNext}
          missing={missing}
        />

        {(rowsLoading || pending) && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Re-measuring…
          </Typography>
        )}

        {/* The guards below all describe a re-measure that happened. With the
            lookup failed there is none, so they would be three confident
            statements about nothing. */}
        {rowsError ? (
          <Box sx={{ mt: 1 }}>
            <PerceptionModelError error={rowsError} />
          </Box>
        ) : (
          <AnchorGuards
            result={result}
            memberCount={members.length}
            translationCount={translationCount}
            emptySelection={next.length === 0}
          />
        )}

        {saveError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {saveError.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!ready || saving}
          onClick={onSave}
        >
          {saving ? 'Saving…' : 'Re-measure and save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnchorEditor;
